/**
 * MyReservationPage — standalone "Reserva de encordados" section
 * (DASHBOARD-SIDEMENU-PROFILES-1-FIX2 · Feb 2026).
 *
 * Player-only · pulls the player's rackets from `localStorage` and
 * reuses the mocked store / service / mode / pricing logic from the
 * exported `ReservationBlock`. No backend calls.
 */
import React, { useState } from "react";
import PanelLayout from "@/components/layout/PanelLayout";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Store, ShieldCheck } from "lucide-react";
import {
  ReservationBlock,
  loadRackets,
  T as RACKET_LAB_T,
} from "@/components/RacketLabPlayerPreview";

const COPY = {
  es: { eyebrow: "Racket Lab", badge: "Preview controlado", title: "Reserva de encordados" },
  en: { eyebrow: "Racket Lab", badge: "Controlled preview", title: "Stringing booking" },
};

export default function MyReservationPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const tx = RACKET_LAB_T[lang] || RACKET_LAB_T.en;
  const copy = COPY[lang] || COPY.en;
  const isPlayer = user?.profile_key === "player";

  const [rackets] = useState(loadRackets);

  if (user && !isPlayer) return <Navigate to="/app" replace />;

  return (
    <PanelLayout>
      <div data-testid="page-my-reservation">
        <header className="flex flex-wrap items-center gap-3 mb-2">
          <Store className="w-6 h-6 text-emerald-700" />
          <div className="tsw-overline">{copy.eyebrow}</div>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold uppercase tracking-wider"
            data-testid="page-my-reservation-badge"
          >
            <ShieldCheck className="w-3 h-3" />{copy.badge}
          </span>
        </header>
        <h1
          className="text-2xl font-bold text-slate-900 mb-6"
          data-testid="page-my-reservation-title"
        >
          {copy.title}
        </h1>

        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6">
          <ReservationBlock tx={tx} rackets={rackets} />
        </div>

        <p className="text-[11px] text-slate-500 mt-4 italic">{tx.legalBlurb}</p>
      </div>
    </PanelLayout>
  );
}
